/**
 * perfMetrics — the client mirror of the backend's closed perf-metric set.
 *
 * MUST stay identical to backend/app/telemetry_metrics.py `PERF_METRICS`. The
 * server rejects the WHOLE perf batch with a 400 if any sample's metric is not
 * in that set, so the beacon filters against this mirror BEFORE sending — an
 * unknown metric is dropped client-side, never sent. Keeping the canonical list
 * in ONE named module (rather than inline in the beacon) makes the parity with
 * the backend reviewable in one place and lets the web-vitals mapper share it.
 *
 * The members:
 *   question_load_ms — app-specific: "ask for next question" → "rendered" (ms).
 *                      Emitted by our own instrumentation, not by web-vitals.
 *   lcp, fid, cls, ttfb, inp — Core Web Vitals (Google's field metrics).
 *
 * NOTE on the installed web-vitals version: web-vitals@4 reports CLS, FCP, FID,
 * INP, LCP, TTFB (the on* API). So `inp` IS now produced by the library and
 * flows through the mapper below to the 'inp' bucket. `fcp` is also produced but
 * is NOT in this set — the mapper maps FCP to null so the beacon skips it. `fid`
 * is DEPRECATED (superseded by INP) but stays in the set while the client
 * dual-reports it; it leaves this mirror when the backend PERF_METRICS drops it.
 */

// A Set for O(1) membership in the beacon's sample filter.
export const PERF_METRICS = new Set([
  'question_load_ms',
  'lcp',
  'fid',
  'cls',
  'ttfb',
  'inp',
]);

/** True iff `metric` is one of the closed PERF_METRICS members. */
export function isPerfMetric(metric) {
  return PERF_METRICS.has(metric);
}

/**
 * Map a web-vitals metric NAME (the `name` field on a web-vitals report object,
 * e.g. 'LCP', 'CLS', 'FID', 'TTFB', 'INP', 'FCP') to its PERF_METRICS member, or
 * `null` when the web-vital has no place in our set (FCP) or isn't recognized.
 *
 * web-vitals reports names in UPPER CASE; PERF_METRICS uses lower case. CLS rides
 * as a unitless ratio (the backend clamps/buckets it on its own scale); the ms
 * vitals ride as milliseconds. Returns null for anything we don't collect so the
 * caller skips it — we never send a metric the server would reject.
 */
export function webVitalToMetric(name) {
  switch (name) {
    case 'LCP': return 'lcp';
    case 'FID': return 'fid';
    case 'CLS': return 'cls';
    case 'TTFB': return 'ttfb';
    case 'INP': return 'inp';
    // FCP is reported by web-vitals but is NOT in PERF_METRICS — skip it.
    case 'FCP': return null;
    default: return null;
  }
}

/** The app-specific question-load metric name (single source for instrumentation). */
export const QUESTION_LOAD_METRIC = 'question_load_ms';
